import { defineStore } from "pinia";
import { useApiCall, invalidateApiCache } from "~/composables/useApiCall";
import { useFeatureManagement } from "~/composables/useFeatureManagement";
import ManageState, {
  RequestedPublications,
  ExcludedPublications,
  ExcludedPublicationItem,
  RequestedPublicationItem,
} from "~/types/ManageStateType";
import AddedMentionAttribute from "~/types/attributes/AddedMentionAttributeType";
import RequestOptions from "~/types/RequestOptionsType";
import { formatDate } from "~/utils/DateFormatter";
import {
  getIcon,
  getPrettyMediaType,
  getStatusValue,
} from "~/utils/ManageUtils";

export const useManageStore = defineStore("manage", {
  state: (): ManageState => ({
    currentTab: "added-mentions",
    loading: false,
    searchText: "",
    sortBy: 1,
    orderBy: 1,
    page: 1,
    total: 0,
    perPage: 10,
    requestedPublications: [],
    mentionNotification: false,
    notificationType: "default",
    notificationValue: "",
    addedMentions: [],
    excludedMentions: [],
    totalExcludedMentions: 0,
    newArticle: null,
    isSearchingArticle: false,
    articleSearchError: false,
    excludedPublications: [],
    excludeMentionErrorKey: "",
    currentPageData: [],
    onManageSearchPage: false,
  }),
  getters: {
    pageStart: (state) => (state.page - 1) * state.perPage + 1,
    pageEnd: (state) =>
      state.page * state.perPage > state.total
        ? state.total
        : state.page * state.perPage,
    sortFields: (state) => {
      switch (state.currentTab) {
        case "added-mentions":
          return ["title", "publication", "media_type"];
        case "excluded-mentions":
          return ["name", "publication", "media_type"];
        case "requested-publications":
          return ["name", "media_type", "status"];
        case "excluded-publications":
          return ["name", "media_type", "status", "url"];
        default:
          return ["name"];
      }
    },
    isAddedMentionLimitReached: (state) => {
      const { getFeatureConfig } = useFeatureManagement();
      const maxAddedArticles = getFeatureConfig<number>(
        "searchProfiles.maxAddedArticles",
      );
      if (!maxAddedArticles) return false;
      return state.addedMentions.length >= maxAddedArticles;
    },
    isExcludedPublicationLimitReached: (state) => {
      const { getFeatureConfig } = useFeatureManagement();
      const maxExcludedDomains = getFeatureConfig<number>(
        "searchProfiles.maxExcludedDomains",
      );
      if (!maxExcludedDomains) return false;
      return state.excludedPublications.length >= maxExcludedDomains;
    },
  },
  actions: {
    reset() {
      this.searchText = "";
      this.sortBy = 1;
      this.orderBy = 1;
      this.page = 1;
      this.total = 0;
    },
    updateSearchText(newText: string) {
      this.searchText = newText;
      this.page = 1;
      this.updateCurrentPageData();
    },
    handleCloseAddedMentionsDrawer() {
      this.newArticle = null;
      this.articleSearchError = false;
    },
    handleSortChange(optionsArr: Array<number>) {
      this.sortBy = optionsArr[0];
      this.orderBy = optionsArr[1];
      this.updateCurrentPageData();
    },
    handlePaginateForward() {
      if (this.page * this.perPage < this.total) {
        this.page += 1;
        this.updateCurrentPageData();
      }
    },
    handlePaginateBackward() {
      if (this.pageStart !== 1) {
        this.page -= 1;
        this.updateCurrentPageData();
      }
    },

    filteredSortedFormattedResultPages(items: any) {
      // Sort the items based on the sort dropdown value
      let filteredItems = [];
      switch (this.sortBy) {
        case 1: // Date
          filteredItems = this.sortNewestToOldest(
            this.filteredResults(items, this.sortFields),
            "created_at",
          );
          break;
        case 2: // Alphabetical
          filteredItems = this.sortAlphabetical(
            this.filteredResults(items, this.sortFields),
            this.currentTab === "added-mentions" ? "publication" : "name",
          );
          break;
        default:
          filteredItems = this.filteredResults(items, this.sortFields);
      }

      // Put the items into pages based on the perPage value
      const rows = Math.ceil(filteredItems.length / this.perPage);
      const result = [];
      for (let i = 0; i < rows; i++) {
        result.push(
          filteredItems.slice(i * this.perPage, (i + 1) * this.perPage),
        );
      }
      this.total = filteredItems.length;
      return result;
    },

    // Filter items by the search term and fields
    filteredResults(items: any, fields: string[]) {
      if (!this.searchText) {
        return items;
      }

      // Normalize data because it could come in differently
      items = items.map((item) => {
        if (item.items) {
          const newItem = { ...item };

          item.items.forEach((item: any) => {
            if (item.label === "Media Type") {
              newItem.media_type = item.value;
            } else if (item.label === "Source") {
              newItem.publication = item.value;
            } else if (item.label === "Status") {
              newItem.status = item.value;
            } else if (item.label === "URL") {
              newItem.url = item.value;
            }
          });

          return newItem;
        } else {
          return item;
        }
      });

      return items.filter((item: any) => {
        return fields.some((field: string) => {
          return item[field]
            ?.toLowerCase()
            .includes(this.searchText.toLowerCase());
        });
      });
    },

    filterMentionsByProfile(mentions: any[], profileId: any) {
      return mentions.filter(
        (mention) => mention.relationships.profile.data.id === profileId,
      );
    },

    sortAlphabetical(items: any[], field: string) {
      return items.sort((a, b) => {
        let returnValue = 0;
        const nameA = a[field]?.toLowerCase();
        const nameB = b[field]?.toLowerCase();

        if (nameA < nameB) {
          returnValue = -1;
        } else if (nameA > nameB) {
          returnValue = 1;
        } else {
          return 0;
        }
        return this.orderBy === 1 ? returnValue : returnValue * -1;
      });
    },

    sortNewestToOldest(items: any[], field: string) {
      return items.sort((a, b) => {
        const dateA: any = new Date(a[field]);
        const dateB: any = new Date(b[field]);
        return this.orderBy === 1 ? dateB - dateA : dateA - dateB;
      });
    },

    async fetchRequestedPublications() {
      this.loading = true;
      let pubs: RequestedPublications = [];
      try {
        const res = await useApiCall("/added_domains", {
          method: "GET",
        });
        if (res && !res.error.value) {
          pubs = res.data._rawValue.data;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.loading = false;
      }
      this.requestedPublications = pubs;
      this.total = pubs.length;
      this.updateCurrentPageData();
    },

    async fetchAddedMentions() {
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );
      const profileId = currentProfile.data?.id;
      this.loading = true;
      try {
        const res = await useApiCall(`/added_articles?profile_id=${profileId}`);
        if (res && !res.error.value) {
          this.addedMentions = res.data.value.data.map((el: any) => ({
            ...el.attributes,
            id: el.id,
          }));
          this.total = res.data.value.data.length;
          this.updateCurrentPageData();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async findArticle(url: string) {
      const date = new Date();
      const formattedDate =
        date.getFullYear() +
        `-` +
        (`${date.getMonth() + 1}`.length < 2
          ? `0${date.getMonth() + 1}`
          : `${date.getMonth() + 1}`) +
        `-` +
        (`${date.getDate()}`.length < 2
          ? `0${date.getDate()}`
          : `${date.getDate()}`);
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );
      const profileId = currentProfile.data?.id;
      this.isSearchingArticle = true;
      this.articleSearchError = false;
      this.newArticle = null;
      try {
        const res = await useApiCall("/article_searches", {
          method: "POST",
          body: {
            data: {
              attributes: {
                filter_date_start: formattedDate,
                filter_date_end: formattedDate,
                url,
              },
              relationships: {
                profile: {
                  data: {
                    id: profileId.toString(),
                    type: "profiles",
                  },
                },
              },
              type: "article-searches",
            },
          },
        });
        if (res && !res.error.value) {
          const searchResult = res.data.value.data.attributes.search_result;
          this.newArticle = {
            ...searchResult,
            in_crawler: true,
            manual_edit: false,
          };
        } else {
          this.articleSearchError = true;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.articleSearchError = true;
      } finally {
        this.isSearchingArticle = false;
      }
    },

    async updateAddedMention() {
      let isArticleUpdated = false;
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );
      const profileId = currentProfile.data?.id;

      const articleId = this.newArticle?.id;
      if (!articleId || !profileId) {
        return false;
      }

      try {
        const res = await useApiCall(`/added_articles/${this.newArticle?.id}`, {
          method: "PATCH",
          body: {
            data: {
              attributes: {
                author: this.newArticle?.author,
                country: this.newArticle?.country,
                created_at: this.newArticle?.created_at,
                in_crawler: this.newArticle?.in_crawler,
                lang: this.newArticle?.lang,
                manual_edit: this.newArticle?.manual_edit,
                media_type: this.newArticle?.media_type,
                publication: this.newArticle?.publication,
                published_on: this.newArticle?.published_on,
                summary: this.newArticle?.summary,
                title: this.newArticle?.title,
                url: this.newArticle?.url,
                licenses: this.newArticle?.licenses,
              },
              relationships: {
                profile: {
                  data: {
                    id: profileId.toString(),
                    type: "profiles",
                  },
                },
              },
              type: "added-articles",
            },
          },
        });

        const message = res?.data?.value?.message;
        if (
          message === "countryLevelAccessFailed" ||
          message === "orgLicenseFailed"
        ) {
          return message;
        } else if (res && !res.error?.value) {
          isArticleUpdated = true;
          invalidateApiCache("/added_articles");
        } else {
          return false;
        }

        this.fetchAddedMentions();
        return isArticleUpdated;
      } catch {
        return false;
      }
    },

    async addMention() {
      let isArticleAdded = false;
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );
      const profileId = currentProfile.data?.id;
      try {
        const res = await useApiCall("/added_articles", {
          method: "POST",
          body: {
            data: {
              attributes: {
                author: this.newArticle?.author,
                country: this.newArticle?.country,
                created_at: this.newArticle?.created_at,
                in_crawler: this.newArticle?.in_crawler,
                lang: this.newArticle?.lang,
                manual_edit: this.newArticle?.manual_edit,
                media_type: this.newArticle?.media_type,
                publication: this.newArticle?.publication,
                published_on: this.newArticle?.published_on,
                summary: this.newArticle?.summary,
                title: this.newArticle?.title,
                url: this.newArticle?.url,
                licenses: this.newArticle?.licenses,
              },
              relationships: {
                profile: {
                  data: {
                    id: profileId.toString(),
                    type: "profiles",
                  },
                },
              },
              type: "added-articles",
            },
          },
        });

        if (res?.error?.value?.data.message.includes("duplicate")) {
          return "duplicate";
        }

        if (res?.data?.value?.message === "countryLevelAccessFailed") {
          return "countryLevelAccessFailed";
        } else if (res?.data?.value?.message === "orgLicenseFailed") {
          return "orgLicenseFailed";
        } else if (res && !res.error.value) {
          isArticleAdded = true;
        } else if (
          String(res.error.value?.cause?.data?.errors[0]?.code).includes(
            "reached_the_added_article_limit",
          )
        ) {
          return "manage.mentionsAndPublications.addMentionLimitError";
        } else {
          return false;
        }

        if (isArticleAdded) {
          invalidateApiCache("/added_articles");
          this.fetchAddedMentions();
        }
        return isArticleAdded;
      } catch {
        return false;
      }
    },

    async deleteAddedMention(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };
      try {
        const resp = await useApiCall(`/added_articles/${id}`, options);
        if (resp && !resp.error.value) {
          invalidateApiCache("/added_articles");
        }
        return resp;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },

    async fetchExcludedPublication() {
      this.loading = true;
      const currentSearchDashboardId = JSON.parse(
        localStorage.getItem("currentSearchDashboard") || "{}",
      )?.data?.id;
      let pubs: ExcludedPublications = [];
      try {
        const res = await useApiCall("/search_dashboards", {
          method: "GET",
        });
        if (res && !res.error.value) {
          pubs = res.data._rawValue.included.filter(
            (item: ExcludedPublicationItem) =>
              item.type === "excluded_domain" &&
              (!currentSearchDashboardId ||
                item.relationships.search_dashboard?.data?.id ===
                  currentSearchDashboardId),
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.loading = false;
      }
      this.excludedPublications = pubs;
      this.total = pubs.length;
      this.updateCurrentPageData();
    },

    async fetchExcludedMentions() {
      const currentProfile = JSON.parse(
        localStorage.getItem("currentProfile") || "{}",
      );
      const profileId = currentProfile.data?.id;

      let mentions = [];
      this.loading = true;

      try {
        const res = await useApiCall(
          `/excluded_articles?profile_id=${profileId}`,
          {
            method: "GET",
          },
        );
        if (res && !res.error.value) {
          mentions = this.filterMentionsByProfile(
            res.data._rawValue.data,
            profileId,
          );
          this.totalExcludedMentions =
            res.data._rawValue.meta.total_excluded_articles;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.loading = false;
      }

      this.excludedMentions = mentions;
      this.total = this.excludedMentions.length;
      this.updateCurrentPageData();
    },

    updateNewArticle(newArticle: AddedMentionAttribute) {
      this.newArticle = newArticle;
    },
    updateCurrentPageData() {
      // If on manage search page, do not update current page data
      if (this.onManageSearchPage) return;

      let currentPageSource: any[] = [];
      if (this.currentTab === "added-mentions") {
        currentPageSource = this.addedMentions;
      } else if (this.currentTab === "excluded-mentions") {
        currentPageSource = this.getMappedExcludedMentions();
      } else if (this.currentTab === "requested-publications") {
        currentPageSource = this.getMappedRequestedPublications();
      } else if (this.currentTab === "excluded-publications") {
        currentPageSource = this.getMappedExcludedPublications();
      }

      this.currentPageData =
        this.filteredSortedFormattedResultPages(currentPageSource)[
          this.page - 1
        ];
    },
    getMappedExcludedMentions() {
      return this.excludedMentions.map((m) => {
        return {
          id: m.id,
          name: m.attributes.title,
          author: m.attributes.author,
          url: m.attributes.url,
          icon: getIcon(m.attributes.media_type),
          created_at: m.attributes.created_at,
          items: [
            {
              label: "manage.mentionsAndPublications.mediaType",
              value: getPrettyMediaType(m.attributes.media_type),
            },
            {
              label: "manage.mentionsAndPublications.sort.dateExcluded",
              value: formatDate(new Date(String(m.attributes.created_at))),
            },
            {
              label: "mentionsTableHeaders.publication",
              value: m.attributes.publication,
            },
          ],
        };
      });
    },
    getMappedRequestedPublications() {
      return this.requestedPublications.map((p: RequestedPublicationItem) => {
        return {
          id: parseInt(p.id, 10),
          name: p.attributes.name,
          url: p.attributes.url,
          icon: getIcon(p.attributes.media_type),
          created_at: p.attributes.created_at,
          items: [
            {
              label: "manage.mentionsAndPublications.mediaType",
              value: getPrettyMediaType(p.attributes.media_type),
            },
            {
              label: "manage.mentionsAndPublications.dateRequested",
              value: formatDate(new Date(String(p.attributes.created_at))),
            },
            {
              label: "manage.mentionsAndPublications.status",
              value: getStatusValue(p.attributes.status),
            },
          ],
        };
      });
    },
    getMappedExcludedPublications() {
      return this.excludedPublications.map((p: ExcludedPublicationItem) => {
        return {
          id: Number(p.id),
          name: String(
            p.attributes.media_type === "web"
              ? p.attributes.name
              : p.attributes.url,
          ),
          icon: getIcon(p.attributes.media_type),
          created_at: p.attributes.created_at,
          items: [
            {
              label: "manage.mentionsAndPublications.mediaType",
              value: getPrettyMediaType(p.attributes.media_type),
            },
            {
              label: "manage.mentionsAndPublications.dateExcluded",
              value: formatDate(new Date(String(p.attributes.created_at))),
            },
            {
              label:
                p.attributes.media_type === "web"
                  ? "manage.mentionsAndPublications.url"
                  : "",
              value: p.attributes.media_type === "web" ? p.attributes.url : "",
            },
          ],
        };
      });
    },
  },
});
